import { AppShell, Burger, Group } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { AssistantFab } from "@/components/assistant/assistant-fab";
import {
	assistantAsideConfig,
	assistantHeaderStyle,
	useAssistantLayout,
	useAssistantSpace,
} from "@/components/assistant/use-assistant-space";
import { GlobalSearch, useGlobalSearch } from "@/components/global-search";
import { Header } from "@/components/header";
import { Sidebar } from "@/components/sidebar";
import { useSidebarFullscreen } from "@/hooks/use-sidebar-fullscreen";
import { useSidebarLayout } from "@/hooks/use-sidebar-layout";
import { useIsDark } from "@/hooks/useIsDark";
import { useSystemMonitor } from "@/hooks/useSystemMonitor";
import { setAksesPrefs } from "@/store/akses";
import { authStore } from "@/store/auth";
import {
	type FormatTanggal,
	i18nStore,
	setDashboardPrefs,
	setFormatTanggal,
	setLang,
} from "@/store/i18n";
import { resetPermissions, setPermissions } from "@/store/permission";

interface MainLayoutProps {
	children: React.ReactNode;
	routeKey?: string;
}

function PageTransition({
	children,
	routeKey,
	enabled,
}: {
	children: React.ReactNode;
	routeKey: string;
	enabled: boolean;
}) {
	const [displayChildren, setDisplayChildren] = useState(children);
	const [displayKey, setDisplayKey] = useState(routeKey);
	const [phase, setPhase] = useState<"idle" | "exit" | "enter">("idle");
	const pendingRef = useRef<{ children: React.ReactNode; key: string } | null>(
		null,
	);

	// displayKey and children intentionally excluded: adding displayKey would
	// cancel enterTimer on every setDisplayKey call; children changes on the
	// same route don't need to trigger the transition animation.
	// biome-ignore lint/correctness/useExhaustiveDependencies: see above
	useEffect(() => {
		if (routeKey === displayKey) return;

		if (!enabled) {
			setDisplayChildren(children);
			setDisplayKey(routeKey);
			return;
		}

		pendingRef.current = { children, key: routeKey };
		setPhase("exit");

		// enterTimer is declared outside so the cleanup closure can cancel it even
		// if exitTimer has already fired before the component unmounts.
		let enterTimer: ReturnType<typeof setTimeout> | null = null;
		const exitTimer = setTimeout(() => {
			if (pendingRef.current) {
				setDisplayChildren(pendingRef.current.children);
				setDisplayKey(pendingRef.current.key);
				pendingRef.current = null;
			}
			setPhase("enter");
			enterTimer = setTimeout(() => setPhase("idle"), 250);
		}, 180);

		return () => {
			clearTimeout(exitTimer);
			if (enterTimer !== null) clearTimeout(enterTimer);
		};
	}, [routeKey, enabled]);

	const glitchClass = enabled
		? phase === "exit"
			? "page-glitch-exit"
			: phase === "enter"
				? "page-glitch-enter"
				: undefined
		: undefined;

	return <div className={glitchClass}>{displayChildren}</div>;
}

export function MainLayout({ children, routeKey = "" }: MainLayoutProps) {
	const dark = useIsDark();
	const { animasiTransisi } = useSnapshot(i18nStore);
	const { user } = useSnapshot(authStore);
	useSystemMonitor();
	const assistantSpace = useAssistantSpace();
	const assistantLayout = useAssistantLayout(true);
	const reducedMotion = useReducedMotion();
	const sidebarLayout = useSidebarLayout(assistantSpace);
	const { opened, toggleMobile, handleMainClick } = useSidebarFullscreen(
		sidebarLayout.mode === "hidden",
		sidebarLayout.hide,
	);
	const { open: searchOpen, setOpen: setSearchOpen } = useGlobalSearch();
	const isRail = sidebarLayout.mode === "rail";

	// Non-admin users inherit display preferences from admin's global settings.
	// user?.role sengaja dipakai (bukan `user`) — efek hanya perlu re-run saat
	// role berubah, bukan tiap kali objek user berubah referensi.
	// biome-ignore lint/correctness/useExhaustiveDependencies: see above
	useEffect(() => {
		if (!user || user.role === "admin") return;
		fetch("/api/umum-preferences")
			.then((r) => (r.ok ? r.json() : null))
			.then((json) => {
				if (!json?.data) return;
				const d = json.data;
				setLang(d.bahasa === "en" ? "en" : "id");
				setFormatTanggal(d.formatTanggal as FormatTanggal);
				setDashboardPrefs({
					refreshOtomatis: d.refreshOtomatis,
					intervalRefresh: d.intervalRefresh,
					tampilkanGrid: d.tampilkanGrid,
					animasiTransisi: d.animasiTransisi,
				});
			})
			.catch(() => {});
	}, [user?.role]);

	useEffect(() => {
		fetch("/api/akses-preferences")
			.then((r) => (r.ok ? r.json() : null))
			.then((json) => {
				if (json?.data) setAksesPrefs(json.data);
			})
			.catch(() => {});
	}, []);

	// user?.id/user?.role sengaja dipakai (bukan `user`) — efek hanya perlu
	// re-run saat identitas/role berubah, bukan tiap kali objek user berubah referensi.
	// biome-ignore lint/correctness/useExhaustiveDependencies: see above
	useEffect(() => {
		if (!user) {
			resetPermissions();
			return;
		}
		fetch("/api/my-permissions")
			.then((r) => (r.ok ? r.json() : null))
			.then((json) => {
				if (Array.isArray(json?.allowed)) setPermissions(json.allowed);
			})
			.catch(() => {});
	}, [user?.id, user?.role]);

	const headerBgColor = dark ? "#11192D" : "#19355E";
	const navbarBgColor = dark ? "#11192D" : "white";
	const mainBgColor = dark ? "#11192D" : "#edf3f8ff";

	return (
		<AppShell
			header={{ height: 60 }}
			navbar={{
				width: sidebarLayout.width,
				breakpoint: "sm",
				collapsed: {
					mobile: !opened,
					desktop: sidebarLayout.mode === "hidden",
				},
			}}
			aside={assistantAsideConfig(assistantSpace)}
			transitionDuration={animasiTransisi && !reducedMotion ? 200 : 0}
			padding="md"
		>
			<GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
			<AppShell.Header
				bg={headerBgColor}
				style={assistantHeaderStyle(assistantLayout.headerInsetEnd)}
			>
				<Group h="100%" px="md" wrap="nowrap">
					<Burger
						opened={opened}
						onClick={toggleMobile}
						hiddenFrom="sm"
						size="sm"
					/>
					<Header
						onSidebarToggle={sidebarLayout.toggle}
						onSearchOpen={() => setSearchOpen(true)}
					/>
				</Group>
			</AppShell.Header>

			<AppShell.Navbar
				p={isRail ? "xs" : "md"}
				bg={navbarBgColor}
				style={{ display: "flex", flexDirection: "column" }}
			>
				<div style={{ flex: 1, overflowY: "auto" }}>
					<Sidebar
						rail={isRail}
						onWiden={sidebarLayout.widen}
						onSearch={() => setSearchOpen(true)}
					/>
				</div>
			</AppShell.Navbar>

			<AppShell.Main
				bg={mainBgColor}
				onClick={handleMainClick}
				style={{
					transition: animasiTransisi ? "background-color 0.3s ease" : "none",
				}}
			>
				<PageTransition routeKey={routeKey} enabled={animasiTransisi}>
					{children}
				</PageTransition>

				<AssistantFab />
			</AppShell.Main>
		</AppShell>
	);
}
